import { DateRange } from '@angular/material/datepicker';
import { Observable } from 'rxjs';

import { ISelectingStrategy, ICheckingStrategy } from './strategy';

export enum FilterID {
	Categories = 'categories',
	Brands = 'brands',
	Markers = 'markers',
	Suppliers = 'suppliers',
	StoreMarkers = 'store_markers',
	StoreGroups = 'store_groups',
	StorePlans = 'plans',
	Stores = 'stores',
	EquipmentTypes = 'types',
	EquipmentProducers = 'producers',
	NotificationsTypes = 'notifications_types',
	PlanogramStatuses = 'statuses',
	ProductsHideOptions = 'hide_options',
	Date = 'date',
	Radio = 'radio',
	CalculationType = 'calculation_type',
	AssortmentTypes = 'assortment_types',
	ProductStatuses = 'product_statuses',
	DemoPlanogramCategories = 'demo-planogram-categories',
	IsMasterPositionExpanded = 'is_master_position_expanded'
}

export enum FilterType { CheckboxTree, RadioTree, Radio, Date }

export interface FilterItem {
	id: number | string,
	value: string | DateRange<Date> | DateRange<string>,
	opened?: boolean,
	loading?: boolean,
	hasChildren?: boolean,
	children?: Array<FilterItem>,
	label?: string,
	data?: any,
	infoLabel?: string
}

export interface Filter {
	id: FilterID
	type: FilterType,
	param: string,
	label: string,
	displayOrder: number,
	data: Array<FilterItem>,
	selectingStrategy: ISelectingStrategy,
	checkingStrategy?: ICheckingStrategy,
	loadChildren?: (id: number) => Observable<Array<FilterItem>>,
	afterCheck?: (filter: Array<Filter>) => void,
	searchQuery?: string,
	search?: (filter: Filter) => Observable<Array<FilterItem>>,
	disabled?: boolean,
	disabledTooltip?: string,
	expanded?: boolean,
	infoLabel?: string,
    emptyMessage?: string
}

export function setDefaults(filters: Array<Filter>, callback: (key: string) => any): void {
	filters.forEach((filter: Filter) => {
		if (filter.type == FilterType.CheckboxTree) {
			filter.checkingStrategy.clear();
			filter.selectingStrategy.clear();
			setSelected(filter, filter.data, callback(filter.param));
			checkFilter(filter, filter.data, callback(filter.param));
			if (filter.afterCheck) filter.afterCheck(filters);
		}
		if (filter.type == FilterType.Radio) {
			const selectedId: string | number = callback(filter.param) ?? -1,
				selectedItem: FilterItem = filter.data.find((item: FilterItem) => item.id == selectedId);
			filter.selectingStrategy.setSelected(selectedItem);
			if (filter.afterCheck) filter.afterCheck(filters);
		}
	})
}

export function setSelected(filter: Filter, elements: Array<FilterItem> = [], selectedIds: Array<number> = []): void {
	elements.forEach((element: FilterItem) => {
		if (selectedIds.includes(element.id as number)) filter.selectingStrategy.setSelected(element);
		if (element.children.length) setSelected(filter, element.children, selectedIds);
	});
}

export function checkFilter(
	filter: Filter,
	elements: Array<FilterItem> = [],
	selected: Array<number> = [],
	parentChecked: boolean = false,
	open: boolean = false
): void {
	elements.forEach((element: FilterItem) => {
		const strategy = filter.checkingStrategy.getKey(element.id as number);
		strategy.checked = selected.includes(element.id as number) || parentChecked;
		if (element.children.length) {
			if (open) element.opened = true;
			element.children.forEach((child: FilterItem) =>
				filter.checkingStrategy.setKey(child.id as number, strategy.checked, false)
			);
			checkFilter(filter, element.children, selected, strategy.checked, open);
			strategy.checked = element.children.every((child: FilterItem) =>
				filter.checkingStrategy.getKey(child.id as number).checked
			);
			strategy.indeterminate = !strategy.checked &&
				element.children.some((child: FilterItem) => filter.checkingStrategy.any(child.id as number));
		} else {
			strategy.indeterminate = false;
		}
	});
}

export function checkMultiFilter(
	filter: Filter,
	elements: Array<FilterItem> = [],
	selected: Array<Array<number>> = [],
	parentCheck: boolean = false,
	parentIds: Array<number> = [],
	parentIndeterminate: boolean = false,
	open: boolean = false
): void {
	for (const element of elements) {
		const strategy = filter.checkingStrategy.getKey(element.id as number);
		strategy.checked = parentCheck ||
			(
				!!selected.length &&
				selected.every((el: Array<number>) =>
					el.includes(element.id as number) ||
					parentIds.some((pId: number) => el.includes(pId as number))
				)
			);
		strategy.indeterminate = !strategy.checked &&
			(
				parentIndeterminate ||
				selected.some((el: Array<number>) => !el.includes(element.id as number)) &&
				selected.some((el: Array<number>) => el.includes(element.id as number))
			);

		if (element.children.length) {
			if (open) element.opened = true;
			element.children.forEach((child: FilterItem) => {
				const checked: boolean = strategy.checked ||
					(
						!!selected.length &&
						selected.every((el: Array<number>) =>
							el.includes(element.id as number) ||
							el.includes(child.id as number)
						)
					);

				filter.checkingStrategy.setKey(child.id as number, checked, strategy.indeterminate && !checked)
			});
			checkMultiFilter(filter, element.children, selected, strategy.checked, parentIds.concat([element.id as number]), strategy.indeterminate, open);

			strategy.checked = element.children.every((child: FilterItem) => filter.checkingStrategy.getKey(child.id as number).checked);
			strategy.indeterminate = strategy.indeterminate ||
				(
					!strategy.checked &&
					element.children.some((child: FilterItem) =>
						filter.checkingStrategy.any(child.id as number) ||
						selected.some((el: Array<number>) => el.includes(child.id as number))
					)
				);
		}
	}
}